(ns manatee.sysadmin.import-subs
  (:require
   [ajax.edn :refer [edn-request-format edn-response-format]]
   [clojure.set :as set]
   [clojure.string :as str]
   [kee-frame.core :as kf]
   [manatee.admin.task-config.subs :as task-config-subs]
   [re-frame.core :as rf]
   [taoensso.timbre :as log]
   [testdouble.cljs.csv :as csv]))

(rf/reg-sub ::fetch (fn [db [_ path]] (get-in db path)))
(rf/reg-event-db ::store (fn [db [_ path val]] (assoc-in db path val)))

;;
;; utils - to be moved elsewhere

(defn path->id
  [path]
  (->> path
       (map #(if (keyword? %) (name %) (-> % str str/lower-case)))
       (str/join "-")))

(defn radio-group
  "Creates a div containing some radio buttons.

   `path` should be a seq/vec of keywords (ideally namespaced) but can also contain other data;
   it determines where in the db the radio selection is stored (using assoc-in).

   `opts` is a map of options; it may contain a :label to add to the div, and a :style for the div.

   `items` needs to be a seq of key/label pairs (e.g. map) and produces a radio button per pair;
   the key is what is stored and fetched from the db, and the value is what is displayed."
  [path opts items]
  (let [{:keys [label style]} opts]
    (->> items
         (map (fn [[key text]] (vector key (path->id (conj path key)) text)))
         (map (fn [[key id text]] [:span {:key text}
                                   [:input {:type "radio", :name id, :value text, :id id
                                            :checked (= @(rf/subscribe [::fetch path]) key)
                                            :on-change #(rf/dispatch [::store path key])}]
                                   [:label {:for id} text]]))
         (into [:div (when style {:style style})
                (when label [:label label])]))))

#_(defn toggle-button
    [!state path opts [false-text true-text]]
    (let [id (path->id path)
          val (get-in @!state path)
          {:keys [label style]} opts]
      [:div (when style {:style style})
       (when label [:label {:for id} label])
       [:button {:id id, :on-click #(swap! !state update-in path not)}
        (if val true-text false-text)]]))

(defn text-box
  "Creates a div containing a text box with optional label.

   `path` should be a seq/vec of keywords (ideally namespaced) but can also contain other data;
   it determines where in the db the text is stored (using assoc-in).

   `opts` is a map of options; it may contain a :label to add to the div, a :style for the div,
   and a :valid? predicate (if this fails, the text is not stored and the dav has a red border)."
  [path opts]
  (let [{:keys [label style valid?]} opts
        val @(rf/subscribe [::fetch path])
        valid? (or (not valid?) (valid? val))]
    [:div (when style {:style style})
     (when label [:label {:for (path->id path)} label])
     [:input {:type "text", :value val, :id (path->id path)
              :style {:border (str "2px solid " (if valid? "black" "red"))
                      :padding 0, :display :inline}
              :on-change #(let [v (.. % -target -value)]
                            (when (valid? v) (rf/dispatch [::store path v])))}]]))

(defn dropdown
  "Creates a div containing a select element with optional label.

   `path` should be a seq/vec of keywords (ideally namespaced) but can also contain other data;
   it determines where in the db the selection key is stored (using assoc-in).

   `opts` is a map of options; it may contain a :label to add to the div, and a :style for the div.

   `items` needs to be a seq of key/label pairs (e.g. map) with the options for the dropdown;
   the key is what is stored and fetched from the path, and the value is what is displayed."
  [path opts items]
  (let [val (or @(rf/subscribe [::fetch path]) (ffirst items))
        {:keys [label style]} opts]
    [:div (when style {:style style})
     (when label [:label {:for (path->id path)} label])
     (->> items
          (map (fn [[v s]] [:option {:value v} s]))
          (into
           [:select {:id (path->id path), :value val
                     :on-change #(rf/dispatch [::store path (keyword (.. % -target -value))])}]))]))

(defn file-picker
  "Creates a div containing a file picker with optional label.

   `path` should be a seq/vec of keywords (ideally namespaced) but can also contain other data;
   it determines where in the db the file is stored (using assoc-in).

   `opts` is a map of options; it may contain a :label to add to the div, a :style for the div,
   and a list of filename :extensions acceptable (single string; comma-delimited e.g. \".csv,.txt\")

   `event`, if truthy, is used as an event vector to rf/dispatch when a file is picked,
   with the `file` (an instance of JS class File) conjed."
  [path opts event]
  (let [{:keys [label style extensions]} opts]
    [:div (when style {:style style})
     (when label [:label {:for (path->id path)} label])
     [:input (cond-> {:id (path->id path), :type "file"
                      :on-change (fn [e]
                                   (let [file (some-> e .-target .-files (aget 0))]
                                     (rf/dispatch [::store path file])
                                     (when event (rf/dispatch (conj event file)))))}
               extensions (assoc :accept extensions))]]))

(defn read-file
  "Use with file-picker to read the file picked.

   `path` should be the same path passed to `file-picker`
   `opts` can contain a :bufsize to limit how much is read,
   a :file-picker to use rather than the one found via the path,
   or a :file to read, rather than the one in the file-picker
   (in which case the path is only used to pass to the proc-fn)

   `event`, if supplied, is used as an event vector to rf/dispatch when the file is read,
    with the path and the contents of the file conjed.

   `fail`, if supplied, is used as an event vector to rf/dispatch if there is an error reading the file,
    with the path and the error conjed."
  [[path opts event fail]]
  (let [{:keys [bufsize file-picker file]} opts
        file (or file
                 (some-> (or file-picker
                             (.getElementById js/document (path->id path)))
                         .-files
                         (aget 0)))
        reader (js/FileReader.)]
    (if file
      (try (set! (.-onloadend reader)
                 (fn [evt] (if (= (.-loaded evt) (.-total evt))
                             (js/setTimeout #(when event (rf/dispatch (conj event path (.. evt -target -result)))) 0)
                             (when fail (rf/dispatch (conj fail path (ex-info "Error reading file: not fully loaded" {})))))))
           (.readAsText reader (if bufsize (.slice file 0 bufsize) file))
           (catch :default e (when fail (rf/dispatch (conj fail path (ex-info "Error reading file" {} e))))))
      (when fail (rf/dispatch (conj fail path (ex-info "No file provided" {})))))))
(rf/reg-fx ::read-file read-file)

;; speech importer

(defn speech-view
  []
  [:span "*Not yet implemented*"])

;; text importer

(defn parse-csv
  [csv-file]
  (let [csv-data (csv/read-csv csv-file)
        lr (flatten (last csv-data))]
    (if (or (empty? lr) (and (= (count lr) 1) (empty? (nth lr 0))))
      (butlast csv-data)
      csv-data)))

;; To create a submission we need: sid, accid, data, orig-msg, assessment-name
;;  data is {:answers ans :results [{:part 1 :answers ans}]}, where ans is [{:id aid :question-number qn :question-id qid}]
;; To create an answer we need: aid, accid, taskid, qn, authorid, sid, data

(def column-options [[:ignore "Ignore"]
                     [:use-qn "Use as Question Number"]
                     [:use-qid "Use as Question ID" :map-qid]
                     [:use-qt "Use as Question Text"]
                     [:use-sid "Use as Submission ID" :map-sid :use-asid]
                     [:use-asid "Use as Submission and Answer ID" :use-sid :use-aid :map-sid :map-aid :map-asid]
                     [:use-aid "Use as Answer ID" :map-aid :use-asid]
                     [:use-at "Use as Answer Text"]
                     [:map-qid "Map to Question ID" :use-qid]
                     [:map-sid "Map to Submission ID" :use-sid :use-asid]
                     [:map-asid "Map to Submission and Answer ID" :use-sid :use-aid :map-sid :map-aid :use-asid]
                     [:map-aid "Map to Answer ID" :use-aid :use-asid]])

(defn column-option-select
  [i]
  (let [columns @(rf/subscribe [::fetch [::sysadmin ::columns]])
        columns (if (< i (count columns))
                  columns
                  (rf/dispatch [::store [::sysadmin ::columns] (->> (repeat :ignore) (concat columns) (take (inc i)) (vec))]))
        cur (get columns i)
        column-assignments (set/difference (set columns) #{:ignore cur})
        column-options (remove #(or (contains? column-assignments (first %))
                                    (contains? column-assignments (get % 2))
                                    (and (zero? i) (str/starts-with? (name (first %)) "use"))) column-options)]
    (dropdown [::sysadmin ::columns i] {} column-options)))

(rf/reg-sub ::can-scan? (fn [db _] (not-empty (filter #(not= :ignore %) (get-in db [::sysadmin ::columns])))))

(defn sample-table
  [csv-data sample-length has-hdr?]
  (let [data (->> csv-data
                  (map-indexed (fn [i datum] (into [(if has-hdr? (if (zero? i) "Row #" i) (inc i))] datum))))
        [hdr rows] (if has-hdr?
                     [(first data) (rest data)]
                     [(->> (first data)
                           (map-indexed (fn [i _] (if (zero? i) "Row #" (str "Column " (inc i))))))
                      data])
        rows (take sample-length rows)]
    [:table.primary {:style {:background-color "#eff" :margin "4px"}}
     [:style ".sample-cell {max-width: 16em; max-height: 5em; overflow: auto; overflow-wrap: break-word}"]
     [:thead.table__header.primary
      (into [:tr]
            (for [[i e] (map-indexed vector hdr)]
              [:th.table__column-name-container.primary e (column-option-select i)]))]
     (into [:tbody.table__body.primary]
           (for [r rows]
             (into [:tr.table__row] (for [e r] [:td.table__field.primary [:div.sample-cell (str e)]]))))]))

(defn sample-view
  []
  (let [filename @(rf/subscribe [::fetch [::sysadmin ::csv-file]])
        sample @(rf/subscribe [::fetch [::sysadmin ::csv-sample]])
        sample-length (or @(rf/subscribe [::fetch [::sysadmin ::sample-length]]) 2)
        has-hdr? @(rf/subscribe [::fetch [::sysadmin ::has-hdr?]])
        csv-data (parse-csv sample)
        tbl (sample-table csv-data sample-length has-hdr?)]
    [:div {:style {:background-color "#dee"}}
     "Sample file contents: "
     (when filename [:span (str "(showing " sample-length " data lines)")])
     (when filename
       [:button {:on-click #(rf/dispatch [::store [::sysadmin ::sample-length] (inc sample-length)])}
        (if (zero? sample-length) "Show a line of data" "Show another line of data")])
     (when (and filename (pos? sample-length))
       [:button {:on-click #(rf/dispatch [::store [::sysadmin ::sample-length] 0])} "Hide data"])
     (cond filename tbl
           filename "Loading..."
           :else "Not Loaded")]))

(rf/reg-event-fx ::file-picked
                 (fn [{:keys [db]} [_]]
                   {:db (-> db (assoc-in [::sysadmin ::csv-sample] nil) (assoc-in [::sysadmin ::csv-scan] nil))
                    :fx [[::read-file [[::sysadmin ::csv-file] {:bufsize 16384} [::head-scanned] [::head-scan-failed]]]]}))
(rf/reg-event-db ::head-scanned (fn [db [_ _path csv-data]] (assoc-in db [::sysadmin ::csv-sample] csv-data)))
(rf/reg-event-db ::head-scan-failed (fn [db [_ _path error]]
                                      (log/error "Error reading file" (ex-cause error))
                                      (assoc-in db [::sysadmin ::csv-sample] [:span {:style {:color "red"}} (ex-message error)])))

(rf/reg-event-fx ::scan-file (fn [{:keys [db]} [_ medium]] {:db (assoc-in db [::sysadmin ::csv-scan] :scanning)
                                                            :fx [[::read-file [[::sysadmin ::csv-file] {} [::submit-file medium] [::read-failure]]]]}))
(rf/reg-event-db ::read-failure (fn [db [_ _path error]]
                                  (log/error "Error reading file" (ex-cause error))
                                  (assoc-in db [::sysadmin ::csv-scan] [:span {:style {:color "red"}} (ex-message error)])))

(defn prep-row [row-data columns] (mapcat (fn [c d] (if (= :ignore c) [] [d])) columns row-data))
(defn prep-data [csv-data columns medium]
  {:cols (->> columns (filter #(not= :ignore %)) (vec))
   :data (map #(prep-row % columns) csv-data)
   :medium medium
   :account (:account-id @(rf/subscribe [::fetch [::sysadmin ::acc-id]]))})

(rf/reg-event-fx ::submit-file
                 (fn [{:keys [db]} [_ medium _path csv-text]]
                   (let [csv-data (parse-csv csv-text)
                         has-hdr? (get-in db [::sysadmin ::has-hdr?])
                         csv-data (cond-> csv-data has-hdr? rest)
                         columns (get-in db [::sysadmin ::columns])]
                     {:http-xhrio {:method :post
                                   :uri (kf/path-for [:api/ingest-submissions])
                                   :format (edn-request-format)
                                   :response-format (edn-response-format)
                                   :params (assoc (prep-data csv-data columns medium) :dryrun? true)
                                   :on-success [::store [::sysadmin ::csv-scan]]
                                   :on-failure [::file-scanned-failure]}})))

(rf/reg-event-db ::file-scanned-failure
                 (fn [db [_ {:keys [response]}]]
                   (assoc-in db [::sysadmin ::csv-scan]
                             [:span {:style {:color "red"}}
                              (str "Error scanning file: " (if-let [err (:error-msg response)] (str err) (str "Server Error - " response)))])))

(defn render-scan
  [scan]
  (if (vector? scan)
    [:div {:style {:background "lightred"}} scan]
    [:div {:style {:background "#eff"}}
     [:h4 "Scan Results"]
     (let [{:keys [matching-questions _unmatched-questions existing-questions undefined-questions
                   discovered-questions _unknown-questions _new-questions mismatched-questions
                   answers new-answers existing-answers mismatched-answers unknown-answers answer-map
                   create _row->qid
                   row-count error-msg error-data]} scan
           already-q (or matching-questions discovered-questions existing-questions)
           new-a (or answers new-answers answer-map)]
       [:div {:style {:display :flex :flex-direction :column :gap "0.5em"}}
        (when row-count [:span (str "Scanned " row-count " rows.")])
        (when already-q [:span (str "Questions already in db: " (count already-q))])
        (when create [:span (str "Found " (count create) " questions to be created.")])
        (when undefined-questions [:span {:style {:color "darkred" :background "lightyellow"}}
                                   (str "Found " (count undefined-questions) " undefined question ids.")])
        (when mismatched-questions [:span {:style {:color "darkred" :background "lightyellow"}}
                                    (str "Found " (count mismatched-questions) " questions with ids and text not matching the DB.")])
        (when mismatched-answers [:span {:style {:color "darkred" :background "lightyellow"}}
                                  (str "Found " (count mismatched-answers) " answers with ids and text not matching the DB.")])
        (when unknown-answers [:span {:style {:color "darkred" :background "lightyellow"}}
                               (str "Found " (count unknown-answers) " unknown answer ids.")])
        (when existing-answers [:span {:style {:color "darkred" :background "lightyellow"}}
                                (str "Answer ids already used: " (count existing-answers))])
        (when new-a [:span (str "Found " (count new-a) " new answers.")])
        (when error-msg [:span {:style {:color "darkred" :background "lightyellow"}}
                         (str "Server reported an error: " error-msg)
                         [:br]
                         (let [{:keys [row]} error-data]
                           (cond (number? row)
                                 (str "Error occurred in row " row " of the CSV file.")

                                 (vector? row)
                                 (let [[r1 r2] row]
                                   (when (and (number? r1) (number? r2))
                                     (str "Error occurred in rows " (min r1 r2) " and " (max r1 r2) " of the CSV file.")))))])])]))

(defn text-view
  []
  [:div
   [file-picker [::sysadmin ::csv-file] {:extensions ".csv"} [::file-picked]]
   (let [sample @(rf/subscribe [::fetch [::sysadmin ::csv-sample]])
         can-scan? @(rf/subscribe [::can-scan?])]
     (if (string? sample)
       [:<>
        [:div "File has header row "
         [:input {:type :checkbox
                  :on-change #(let [checked? (.. % -target -checked)]
                                (rf/dispatch [::store [::sysadmin ::has-hdr?] checked?]))}]]
        (sample-view)
        [:button {:on-click #(rf/dispatch [::scan-file :text])
                  :disabled (not can-scan?)}
         (if can-scan? "Scan file" "Configure columns to enable scanning")]
        (let [scan @(rf/subscribe [::fetch [::sysadmin ::csv-scan]])]
          (case scan
            :scanning "Scanning..."
            nil ""
            (render-scan scan)))]

       sample))])

;;
;; main view

(defn view
  []
  (let [task-list (->> @(rf/subscribe [::task-config-subs/active-task-configs])
                       (map (juxt :id :task-name))
                       (sort-by second))
        style {:display :flex :flex-direction :column :background-color "#eee"}]
    (fn []
      [:div.panel {:class [:primary :admin-page__panel]}
       [:div {:style {:gap "1em" :display :flex :flex-direction :row}}
        [radio-group [::sysadmin ::medium] {:style style :label "Medium:"} {:speech "Speech" :text "Text"}]
        [text-box [::sysadmin ::acc-id] {:style style :label "Account ID:" :valid? #(and (string? %) (re-matches #"^[A-Za-z0-9_]*$" %))}]
        [text-box [::sysadmin ::assessment] {:style style :label "Assessment Name:" :valid? #(and (string? %) (re-matches #"^[A-Za-z0-9_]*$" %))}]
        [dropdown [::sysadmin ::tcid] {:style style :label "Task Config:"} task-list]]
       (case @(rf/subscribe [::fetch [::sysadmin ::medium]])
         :speech [speech-view]
         :text [text-view]
         nil [:span "Please select medium."])])))
